import moment from 'moment';

import { resolveFieldValue } from '../candidate_application/field-paths';
import { compareDates, validateDateRangeCoverage } from './date-validation-utils';

const functionValidators = {
  dateAfter: {
    validate: (value, params, formValues, fieldPath) => {
      if (!value || !params?.dependsOnFields) return true;

      // Create context based on field path
      const context = {};
      const pathParts = fieldPath.split('.');

      if (pathParts.length === 3) {
        // We're in an array (e.g., "academic_qualifications.0.qualification_end_date")
        context.parentField = { code: pathParts[0] };
        context.arrayIndex = parseInt(pathParts[1]);
      }

      const compareValue = resolveFieldValue(params.dependsOnFields[0], formValues, context);
      if (compareValue == null || compareValue === '') return true; // skip if compareValue is null or empty

      return compareDates(value, compareValue, 'after');
    },
  },

  dobMatch: {
    validate: (value, params, formValues) => {
      // value is the ID's DOB (from extracted fields)
      // Compare with the candidate's DOB from formValues
      if (!value) return true; // Skip if no ID DOB

      const candidateDobField = params?.dependsOnFields?.[0] || 'dob';
      const candidateDob = formValues[candidateDobField];

      if (!candidateDob) return true; // Skip if no candidate DOB to compare

      // Parse both dates for comparison
      // Both dates should be in DD/MM/YYYY format from Regula and our date selector
      const idDob = moment(value, 'DD/MM/YYYY');
      const candDob = moment(candidateDob, 'DD/MM/YYYY');

      if (!idDob.isValid() || !candDob.isValid()) {
        return true; // Skip validation if dates are invalid
      }

      if (idDob.isSame(candDob, 'day')) {
        return true;
      }

      // Custom error message or default
      return (
        params?.errorMessage ||
        `The date of birth on your ID (${value}) does not match the date of birth you provided in the Personal Details section (${candidateDob}), please ensure your date of birth is correct. If you need to update your date of birth in the Personal Details section, use the Save and Exit button in the top right corner to exit this page.`
      );
    },
  },

  // ------------ Wrapper validators ------------ //
  // No value is passed to these validators, just params and formValues

  addressHistory: {
    validate: (params, formValues) => {
      // Get current address from date
      if (!formValues.current_address_residence_from) {
        return 'Current address from date is required';
      }

      const currentAddressFrom = moment(formValues.current_address_residence_from, 'DD/MM/YYYY');
      if (!currentAddressFrom.isValid()) {
        return 'Current address from date is invalid';
      }
      // Calculate minimum past date (default 10 years)
      const yearsToCheck = params?.requiredAddressHistory || 10;
      const minPastDate = moment().subtract(yearsToCheck, 'years');

      // If current address covers entire period, we're done
      if (currentAddressFrom.isSameOrBefore(minPastDate)) {
        return true;
      }

      // Create current address range (from start date to now)
      const currentAddressRange = {
        from: currentAddressFrom,
        to: moment(),
      };

      // If we're here, we need to check previous addresses
      if (
        !Array.isArray(formValues.previous_addresses) ||
        formValues.previous_addresses.length === 0
      ) {
        return `Previous addresses are required to cover the last ${yearsToCheck} years`;
      }

      // Convert previous addresses to date ranges and include current address
      const dateRanges = [
        currentAddressRange,
        ...formValues.previous_addresses.map(address => ({
          from: moment(address.previous_address_residence_from, 'DD/MM/YYYY'),
          to: moment(address.previous_address_residence_to, 'DD/MM/YYYY'),
        })),
      ];

      const result = validateDateRangeCoverage({
        startDate: moment(),
        minPastDate,
        dateRanges,
        toleranceMonths: params?.toleranceMonths,
      });
      // If validation fails, return the error message string
      // If validation passes, return true
      if (result.isValid) {
        return true;
      }
      // Return the error message, or a default message if error is missing
      return result.error || 'Validation failed';
    },
  },

  addressesMustInclCountry: {
    validate: (params, formValues) => {
      const requiredCountry = params?.mustIncludeCountry;
      if (!requiredCountry) {
        return true;
      }

      if (formValues.residential_address?.country === requiredCountry) {
        return true;
      }

      if (formValues.postal_address?.country === requiredCountry) {
        return true;
      }

      const previousAddresses = formValues.previous_addresses;
      if (Array.isArray(previousAddresses)) {
        const hasRequiredCountry = previousAddresses.some(
          address => address.previous_address?.country === requiredCountry,
        );

        if (hasRequiredCountry) {
          return true;
        }
      }

      const errorMessage = `Your address history must include a ${requiredCountry} address`;
      return errorMessage;
    },
  },
};

export default functionValidators;
